// import { httpsCallable, getFunctions } from 'firebase-shim';
import { SmtpSettings } from '../services/settingsService';

/**
 * Service for sending emails with invoice attachments
 */
export class EmailService {
  /**
   * Send an email with an invoice PDF attachment
   * @param to Email recipient
   * @param subject Email subject
   * @param body Email body (HTML supported)
   * @param pdfData PDF file as Uint8Array
   * @param fileName Filename for the PDF attachment
   * @returns Promise that resolves when email is sent
   */
  public static async sendInvoiceEmail(
    to: string,
    subject: string,
    body: string,
    pdfData: Uint8Array,
    fileName: string = 'invoice.pdf',
    smtpSettings?: SmtpSettings
  ): Promise<any> {
    try {
      // Get API URL from environment variables, fallback to local development port
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      if (apiUrl) {
        console.log('Sending email via SMTP Backend Service...');
        // Convert PDF to base64
        const base64Pdf = this.uint8ArrayToBase64(pdfData);
        
        // Structure payload for SMTP backend
        const payload = {
          to,
          subject,
          html: body,
          attachments: [
            {
              filename: fileName,
              content: base64Pdf,
              contentType: 'application/pdf',
              encoding: 'base64'
            }
          ],
          smtpConfig: smtpSettings // Include custom SMTP config if provided
        };
        
        const response = await fetch(`${apiUrl}/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // 'Authorization': `Bearer ${token}` // If auth is needed later
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          throw new Error(`SMTP API returned ${response.status}`);
        }
        
        return { success: true, method: 'smtp-backend' };
      } else {
        console.warn('API URL not configured. Falling back to email client.');
        this.openEmailClient(to, subject, body);
        return { success: true, method: 'client-fallback' };
      }
    } catch (error) {
      console.error('Error sending email via SMTP:', error);
      console.log('Falling back to local email client.');
      this.openEmailClient(to, subject, body);
      return { success: false, error };
    }
  }

  /**
   * Send a basic email without attachments
   */
  public static async sendBasicEmail(
    to: string,
    subject: string,
    body: string,
    smtpSettings?: SmtpSettings
  ): Promise<any> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      if (apiUrl) {
        console.log('Sending basic email via SMTP Backend Service...');
        
        const payload = {
          to,
          subject,
          html: body,
          smtpConfig: smtpSettings
        };
        
        const response = await fetch(`${apiUrl}/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          throw new Error(`SMTP API returned ${response.status}`);
        }
        
        return { success: true, method: 'smtp-backend' };
      } else {
        console.warn('API URL not configured. Falling back to email client.');
        this.openEmailClient(to, subject, body);
        return { success: true, method: 'client-fallback' };
      }
    } catch (error) {
      console.error('Error sending basic email:', error);
      console.log('Falling back to local email client.');
      this.openEmailClient(to, subject, body);
      return { success: false, error };
    }
  }

  /**
   * Test the SMTP connection using the provided settings
   * @param smtpSettings SMTP Configuration to test
   * @returns Promise resolving to success/failure
   */
  public static async testConnection(smtpSettings: SmtpSettings): Promise<any> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      console.log('Testing SMTP connection...');
      
      const payload = {
        testOnly: true, // Tell backend this is just a connection test
        smtpConfig: smtpSettings
      };
      
      const response = await fetch(`${apiUrl}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `API returned ${response.status}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error('SMTP test connection failed:', error);
      throw error;
    }
  }

  /**
   * Fallback method to open user's email client
   * @param to Email recipient
   * @param subject Email subject
   * @param body Email body
   */
  private static openEmailClient(to: string, subject: string, body: string): void {
    // Create a mailto URL
    const mailtoUrl = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Open the URL in a new window/tab
    const newWindow = window.open(mailtoUrl, '_blank');
    
    // Alert the user if the popup was blocked by the browser
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      alert("Your browser's popup blocker prevented the email client from opening. Please allow popups for this site to automatically open your email app.");
    }
  }

  /**
   * Convert Uint8Array to base64 string
   * @param data Uint8Array data
   * @returns Base64 encoded string
   */
  private static uint8ArrayToBase64(data: Uint8Array): string {
    const binary = Array.from(data)
      .map(byte => String.fromCharCode(byte))
      .join('');
    return btoa(binary);
  }
}

export default EmailService; 
